/** Invoices page — guardian's invoice list. */

import React from 'react';
import { usePortalInvoices, type PortalInvoice } from '../api/portal';

const statusColour: Record<string, string> = {
  paid:   '#10b981',
  issued: '#6366f1',
  draft:  '#6b7280',
  overdue:'#ef4444',
  voided: '#9ca3af',
};

function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount / 100);
}

const InvoiceRow: React.FC<{ inv: PortalInvoice }> = ({ inv }) => {
  const colour = statusColour[inv.status] ?? '#6b7280';
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto auto',
        gap: '0.5rem',
        alignItems: 'center',
        padding: '0.875rem 1rem',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        marginBottom: '0.5rem',
      }}
      data-testid={`candeza-portal-invoice-row-${inv.id}`}
    >
      <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{inv.issue_date}</span>
      <strong style={{ fontSize: '0.9375rem' }}>{inv.number ?? inv.id.slice(-6)}</strong>
      <strong style={{ textAlign: 'right' }}>{formatAmount(inv.total, inv.currency)}</strong>
      <span
        style={{
          background: colour + '22',
          color: colour,
          padding: '2px 8px',
          borderRadius: 12,
          fontSize: '0.75rem',
          fontWeight: 600,
          textTransform: 'capitalize',
        }}
      >
        {inv.status}
      </span>
    </div>
  );
};

const InvoicesPage: React.FC = () => {
  const { data, isLoading, isError } = usePortalInvoices();
  const invoices = data?.items ?? [];

  if (isLoading) {
    return <div data-testid="candeza-portal-invoices-loading">Loading invoices…</div>;
  }
  if (isError) {
    return (
      <div data-testid="candeza-portal-invoices-error" style={{ color: '#ef4444' }}>
        Could not load invoices.
      </div>
    );
  }

  return (
    <div data-testid="candeza-portal-invoices-page">
      <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1.25rem' }}>
        Invoices
      </h1>
      {invoices.length === 0 ? (
        <p data-testid="candeza-portal-invoices-empty" style={{ color: '#6b7280' }}>
          No invoices found.
        </p>
      ) : (
        <div data-testid="candeza-portal-invoices-list">
          {invoices.map(inv => <InvoiceRow key={inv.id} inv={inv} />)}
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;


